import apiClient from "@/api/client";
import type {
  CareAffectedProject,
  CareArrangement,
  CareFlag,
  CareNote,
  CareScope,
} from "@/features/clients/types";

const wrap = <T>(res: { data: { data: T } }) => res.data.data;

// Parametry listowania = adresowanie (dokładnie jeden poziom).
const scopeParams = (scope: CareScope) => {
  const params: Record<string, string> = {};
  if (scope.client) params.client = scope.client;
  if (scope.branch) params.branch = scope.branch;
  if (scope.sub_branch) params.sub_branch = scope.sub_branch;
  return params;
};

export const careApi = {
  flags: {
    list: (scope: CareScope) =>
      apiClient
        .get<{ data: CareFlag[] }>("/client-care/flags/", { params: scopeParams(scope) })
        .then(wrap),
    create: (data: CareScope & { label: string; reason?: string }) =>
      apiClient.post<{ data: CareFlag }>("/client-care/flags/", data).then(wrap),
    update: (id: string, data: Partial<Pick<CareFlag, "label" | "reason" | "is_active">>) =>
      apiClient.patch<{ data: CareFlag }>(`/client-care/flags/${id}/`, data).then(wrap),
    delete: (id: string) => apiClient.delete(`/client-care/flags/${id}/`),
  },

  notes: {
    list: (scope: CareScope) =>
      apiClient
        .get<{ data: CareNote[] }>("/client-care/notes/", { params: scopeParams(scope) })
        .then(wrap),
    create: (data: CareScope & { text: string; project?: string | null }) =>
      apiClient.post<{ data: CareNote }>("/client-care/notes/", data).then(wrap),
    delete: (id: string) => apiClient.delete(`/client-care/notes/${id}/`),
  },

  arrangements: {
    list: (scope: CareScope) =>
      apiClient
        .get<{ data: CareArrangement[] }>("/client-care/arrangements/", {
          params: scopeParams(scope),
        })
        .then(wrap),
    create: (
      data: Pick<CareScope, "branch" | "sub_branch"> & {
        kind: CareArrangement["kind"];
        text?: string;
        step_label?: string;
        submit_sla_days?: number | null;
        resolve_sla_days?: number | null;
      },
    ) => apiClient.post<{ data: CareArrangement }>("/client-care/arrangements/", data).then(wrap),
    update: (
      id: string,
      data: Partial<Pick<CareArrangement, "kind" | "text" | "step_label" | "is_active" | "submit_sla_days" | "resolve_sla_days">>,
    ) =>
      apiClient
        .patch<{ data: CareArrangement }>(`/client-care/arrangements/${id}/`, data)
        .then(wrap),
    delete: (id: string) => apiClient.delete(`/client-care/arrangements/${id}/`),

    // Trwające projekty ZE bez kamienia tego ustalenia + potwierdzane dokładanie.
    affected: (id: string) =>
      apiClient
        .get<{ data: { arrangement: { id: string; step_label: string; scope_label: string }; projects: CareAffectedProject[] } }>(
          `/power-projects/care-arrangements/${id}/affected/`,
        )
        .then(wrap),
    apply: (id: string, connections?: string[]) =>
      apiClient
        .post<{ data: { created: { connection_id: string; milestone_id: string; label: string }[] }; meta: { count: number } }>(
          `/power-projects/care-arrangements/${id}/apply/`,
          connections && connections.length ? { connections } : {},
        )
        .then((r) => ({ created: r.data.data.created, count: r.data.meta.count })),
  },
};
